import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Copyright from "../components/CopyRight";
import * as React from "react";
import useAuth from "../hooks/useAuth";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Title from '../components/Title';
import { useEffect, useState } from "react";
import CopyToClipboard from "../components/CopyToClipboard";
import { useTSGUser } from "../contexts/TSGUserContext";
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Snackbar, Stack, TextField } from "@mui/material";
import { useCompany } from "../hooks/useCompany";
import { Geography } from "../models/DataSource";
import geographyData from '../assets/data/geography.json';
import { APIEndpoint, EndpointType, httpPut } from "../utils/apiService";
import { UsersResponse } from "../models/UsersResponse";
import CloseIcon from "@mui/icons-material/Close";
import { Storefront } from "../models/StorefrontResponse";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any; // Needed for Hubspot

interface Props {
  storefront: Storefront
}

const Profile = (props: Props) => {
  const [geography, setGeography] = useState<Geography>({ state: {}, county: {}, metro_area: {}, scf: {} });
  const { user } = useAuth();
  const { tsgUser } = useTSGUser();
  const { company } = useCompany(tsgUser?.companyID || "");
  const { company: storefrontCompany } = useCompany(props.storefront.companyID || "");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [zip, setZip] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMsg, setSnackbarMsg] = useState("")

  useEffect(() => {
    // Log Pageview with Hubspot
    const _hsq = window._hsq = window._hsq || [];
    _hsq.push(['setPath', '/profile']);
    _hsq.push(['trackPageView']);

    // Load geography data 
    setGeography(geographyData as Geography);
  }, []);

  useEffect(() => {
    // Set default values from TSG User context
    setFirstName(tsgUser?.firstName || "");
    setLastName(tsgUser?.lastName || "");
    setAddress(tsgUser?.customerAddress?.address1 || "");
    setCity(tsgUser?.customerAddress?.city || "");
    setState(tsgUser?.customerAddress?.province || "");
    setZip(tsgUser?.customerAddress?.zip || "");
    setPhone(tsgUser?.phone || "");
    setEmail(tsgUser?.email || "");
  }, [tsgUser]);

  function Role() {
    try {
      if (user.signInUserSession.accessToken.payload['cognito:groups'] != undefined) {
        if (user.signInUserSession.accessToken.payload['cognito:groups'].length > 0) {
          if (user.signInUserSession.accessToken.payload['cognito:groups'].length === 1) {
            return user.signInUserSession.accessToken.payload['cognito:groups'][0];
          }
        }
      }

      if (String(user.attributes.email).toLowerCase().includes("@theshare.group")) {
        return "Employee";
      }

    } catch (err) {
      console.log("error: " + err)
    }
    return "Customer"
  }

  function Company() {
    if (tsgUser?.companyID == "") {
      return "No company association"
    } else {
      if (company && company.name) {
        return company.name;
      }
    }
  }

  const saveChanges = () => {
    const newUser = tsgUser

    newUser.firstName = firstName
    newUser.lastName = lastName
    newUser.phone = phone
    newUser.email = email
    if (newUser.customerAddress) {
      newUser.customerAddress.address1 = address;
      newUser.customerAddress.city = city;
      newUser.customerAddress.province = state;
      newUser.customerAddress.zip = zip;
    } else {
      newUser.customerAddress = {
        address1: address,
        address2: "",
        city: city,
        company: "",
        province: state,
        provinceCode: "",
        zip: zip,
        country: "US", // Default to US
        countryCode: "",
        latitude: "",
        longitude: "",
      };
    }

    const apiURL = APIEndpoint(EndpointType.Users) + "/" + newUser.id;
    httpPut(apiURL, JSON.stringify(newUser))
      .then((data) => {
        const response = data as UsersResponse;

        if (response.status == 'error') {
          setSnackbarMsg(`Error updating customer: ${response.errorMessage}`);
          setOpenSnackbar(true);
        } else {
          setSnackbarMsg("Profile updated successfully")
          setOpenSnackbar(true)
        }
      })
      .catch((error) => {
        setSnackbarMsg(`List was not saved - error: ${error as string}`)
        setOpenSnackbar(true)
      });
  }

  const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
  };

  const snackbarAction = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleSnackbarClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            {/* Search */}
            <Title>Profile</Title>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                <Stack spacing={2} alignItems="left">
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 1 }}>
                    <TextField id="firstName" label="First Name" value={firstName} sx={{ width: 300 }} onChange={(e) => setFirstName(e.target.value)}></TextField>
                    <TextField id="lastname" label="Last Name" value={lastName} sx={{ width: 300 }} onChange={(e) => setLastName(e.target.value)}></TextField>
                  </Stack>
                  <TextField id="phone" label="Phone" value={phone} sx={{ width: 300, mt: 2, mb: 1 }} onChange={(e) => setPhone(e.target.value)} ></TextField>
                  <TextField id="email" label="Email" value={email} sx={{ width: 400, mt: 2, mb: 1 }} onChange={(e) => setEmail(e.target.value)}></TextField>
                  <Box >
                    <TextField id="address" label="Address" value={address} sx={{ width: 400, mt: 0, mb: 1 }} onChange={(e) => setAddress(e.target.value)}></TextField>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                      <TextField id="city" label="City" value={city} sx={{ width: 300 }} onChange={(e) => setCity(e.target.value)}></TextField>
                      <FormControl fullWidth size="small" sx={{ width: 200 }}>
                        <InputLabel>State</InputLabel>
                        <Select label="Values" onChange={(e) => setState(e.target.value)} size="medium" value={state} MenuProps={{
                        }}>
                          {Object.entries(geography.state).map(([key, value]) => (
                            <MenuItem value={key} key={key}>{value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField id="zip" label="Zip" value={zip} sx={{ width: 100 }} onChange={(e) => setZip(e.target.value)}></TextField>
                    </Stack>
                  </Box>
                </Stack>
                <Table size="small" sx={{ mt: 2 }}>
                  <TableBody>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell><Role /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>
                        {user.username}<CopyToClipboard value={user.username} /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Storefront</TableCell>
                      <TableCell>{storefrontCompany && storefrontCompany.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>{Company()}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Grid item xs={3}>
                  <Button variant="contained" color="primary" sx={{ mt: 3, ml: 1 }} onClick={saveChanges}>
                    Save Changes
                  </Button>
                </Grid>
              </Paper>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Copyright sx={{ pt: 4 }} />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMsg}
        action={snackbarAction}
      />
    </Container>
  );
};

export default Profile;